export const MachineryType = {
    TRACTOR: 'TRACTOR',
    SPRAYER: 'SPRAYER',
    PLANTER: 'PLANTER',
    PLATFORM: 'PLATFORM',
    GRAIN_CART: 'GRAIN_CART',
    HARVESTER: 'HARVESTER',
    MILL: 'MILL',
    OTHER: 'OTHER',
} as const satisfies Record<string, string>;

export type MachineryType = typeof MachineryType[keyof typeof MachineryType];

export const LegalRegime = {
    AF: 'AF',
    PLEDGE: 'PLEDGE',
    MORTGAGE: 'MORTGAGE',
    FIDUCIARY_ASSIGNMENT: 'FIDUCIARY_ASSIGNMENT',
} as const satisfies Record<string, string>;

export type LegalRegime = typeof LegalRegime[keyof typeof LegalRegime];

export const CostType = {
    MACHINERY_RTD: 'MACHINERY_RTD',
    MACHINERY_RI: 'MACHINERY_RI',
} as const satisfies Record<string, string>;

export type CostType = typeof CostType[keyof typeof CostType];

export interface MachineryOwner {
    personId: string;
    name: string;
    document: string;
}

export interface MachineryCost {
    id?: number;
    costType: CostType;
    value: number;
    costDate: string;
    registrationNumber?: string;
    registrationDate?: string;
    description?: string;
}

export interface MachineryObligation {
    creditOperationId: string;
    allocatedPercentage: number | null;
    status: string;
    statusFormatted?: string;
}

export interface MachineryCustody {
    address: string | null;
    city: string | null;
    state: string | null;
}

export interface MachineryListItem {
    id: string;
    name: string;
    description: string | null;
    machineryType: MachineryType;
    machineryTypeFormatted: string;
    brand: string | null;
    model: string | null;
    yearManufacture: number | null;
    yearModel: number | null;
    serialNumber: string | null;
    finameCode: string | null;
    color: string | null;
    legalRegime: LegalRegime | null;
    legalRegimeFormatted: string;
    invoiceValue: number | null;
    custodyCity: string | null;
    custodyState: string | null;
    archived: boolean;
    discharged: boolean;
    statusFormatted: string;
    createdAt: string;
    updatedAt: string;
}

export interface MachineryDetail extends MachineryListItem {
    custody: MachineryCustody;
    rtdRegistryOffice: string | null;
    registryOffice: string | null;
    registryState: string | null;
    registrationNumber: string | null;
    comments: string | null;
    owners: MachineryOwner[];
    costs: MachineryCost[];
    obligations: MachineryObligation[];
    dischargeDate: string | null;
    dischargeValue: number | null;
}

export const MACHINERY_TYPE_OPTIONS = [
    { value: MachineryType.TRACTOR, label: 'Trator' },
    { value: MachineryType.SPRAYER, label: 'Pulverizador' },
    { value: MachineryType.PLANTER, label: 'Plantadeira' },
    { value: MachineryType.PLATFORM, label: 'Plataforma' },
    { value: MachineryType.GRAIN_CART, label: 'Graneleira' },
    { value: MachineryType.HARVESTER, label: 'Colheitadeira' },
    { value: MachineryType.MILL, label: 'Moinho' },
    { value: MachineryType.OTHER, label: 'Outro' },
];

export const LEGAL_REGIME_OPTIONS = [
    { value: LegalRegime.AF, label: 'Alienação Fiduciária' },
    { value: LegalRegime.PLEDGE, label: 'Penhor' },
    { value: LegalRegime.MORTGAGE, label: 'Hipoteca' },
    { value: LegalRegime.FIDUCIARY_ASSIGNMENT, label: 'Cessão Fiduciária' },
];

export const COST_TYPE_OPTIONS = [
    { value: CostType.MACHINERY_RTD, label: 'RTD - Registro de Títulos e Documentos' },
    { value: CostType.MACHINERY_RI, label: 'RI - Registro de Imóveis' },
];

// Maximum number of owners a machinery can have. Single source of truth,
// reused by the form (UI) and the page-form hook (validation) to avoid drift.
export const MAX_OWNERS = 20;
